// NPM Imports
import React, { Component } from "react";
import ReactHtmlParser from "react-html-parser";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip, Input } from "@revvsales/components";

import AddComponentBlocks from "./AddComponentBlocks";

// Internal Imports
import {
  emptyTable,
  emptyEditor,
  emptyImage,
  emptyQuoteTablePlaceholder,
  lineSpace,
  pageBreak,
  emptyCoverpage,
  emptySignatureSpace,
  googleSheetSpace,
  emptyPaymentButton,
} from "../../utils/documentUtils";

import { readDataFromLocalStorage } from "../../utils/globalUtils";

import { DOCUMENTS, BILLING } from "../../constants";

// Destructuring
const { DOCUMENT_COMPONENT_TYPES } = DOCUMENTS;
const { EXCLUDE_QUOTE_TABLE_PLANS } = BILLING;

class AddComponents extends Component {
  blockWrapperRef = React.createRef();

  _getButtons = (mode, userSignUpInfo) => {
    const components = DOCUMENTS.ADD_COMPONENTS[mode];
    let buttons = DOCUMENTS.BUTTONS.filter(button =>
      components.includes(button.id),
    );
    const { addComponentType } = this.props;
    // Filter the buttons based on "Component type"
    if (addComponentType === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER) {
      buttons = buttons.filter(
        btn =>
          !(
            btn.id === "add-dealtable" ||
            btn.id === "add-page-break" ||
            btn.id === "add-quotetable-placeholder" ||
            btn.id === "add-signature-space" ||
            btn.id === "add-google-sheet" ||
            btn.id === "add-payment-button"
          ),
      );
    } else if (this.props.isPDFDocument) {
      buttons = buttons.filter(
        btn =>
          !(
            btn.id === "add-text" ||
            btn.id === "add-table" ||
            btn.id === "add-image" ||
            btn.id === "add-line-space" ||
            btn.id === "add-dealtable" ||
            btn.id === "add-page-break" ||
            btn.id === "add-quotetable-placeholder" ||
            btn.id === "add-google-sheet" ||
            btn.id === "add-payment-button"
          ),
      );
    }
    // Filter the buttons based on "Billing Plan"
    if (
      userSignUpInfo &&
      EXCLUDE_QUOTE_TABLE_PLANS.indexOf(userSignUpInfo.billing_plan) > -1
    ) {
      buttons = buttons.filter(
        btn =>
          !(
            btn.id === "add-dealtable" ||
            btn.id === "add-quotetable-placeholder"
          ),
      );
    }
    return buttons;
  };

  _getStylingButtons = mode => {
    const components = DOCUMENTS.ADD_COMPONENTS[mode];
    const stylingButtons = DOCUMENTS.DOCUMENT_STYLINGS.filter(button =>
      components.includes(button.id),
    );
    return stylingButtons;
  };

  // add a new component
  _addNewComponent = id => {
    let section = null;
    const { sectionList } = this.props;
    switch (id) {
      case "add-table":
        section = emptyTable(
          this.props.mode,
          sectionList.filter(sec => sec.type === DOCUMENT_COMPONENT_TYPES.TABLE)
            .length,
        );
        break;
      case "add-text":
        section = emptyEditor(this.props.mode);
        break;
      case "add-dealtable":
        section = emptyQuoteTablePlaceholder();
        break;
      case "add-image":
        section = emptyImage(
          sectionList.filter(sec => sec.type === DOCUMENT_COMPONENT_TYPES.IMAGE)
            .length,
        );
        break;
      case "add-quotetable-placeholder":
        section = emptyQuoteTablePlaceholder();
        break;
      case "add-page-break":
        section = pageBreak();
        break;
      case "add-line-space":
        section = lineSpace();
        break;
      case "add-cover-page":
        section = emptyCoverpage(this.props.mode);
        break;
      case "add-signature-space":
        section = emptySignatureSpace(
          sectionList.filter(
            sec =>
              sec.type === DOCUMENT_COMPONENT_TYPES.EMPTY_SIGNATURE_CONTAINER,
          ).length,
        );
        break;
      case "add-google-sheet":
        section = googleSheetSpace(
          sectionList.filter(
            sec => sec.type === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET,
          ).length,
        );
        break;
      case "add-payment-button":
        section = emptyPaymentButton(
          sectionList.filter(
            sec => sec.type === DOCUMENT_COMPONENT_TYPES.STRIPE_PAYMENT_BUTTON,
          ).length,
        );
        break;
      default:
        break;
    }
    return section;
  };

  // add component by clicking on buttons
  _addComponent = (event, id) => {
    const {
      // sectionList,
      addNewComponent,
      updateSection,
      showCoverpage,
      coverpage,
      coverpageThemeAction,
    } = this.props;
    if (coverpage && coverpage.data) {
      const oldSection = { ...coverpage };

      coverpage.data = {
        ...coverpage.data,
        attributes: {
          ...coverpage.data.attributes,
          isOpen: !showCoverpage,
        },
      };

      if (!showCoverpage) {
        coverpageThemeAction("OpenThemePane");
      }
      updateSection(coverpage, undefined, false, oldSection);
    } else {
      const newSection = this._addNewComponent(id);
      // newSection.autoFocus = true;
      if (newSection) {
        if (newSection.type === "CoverPage") {
          coverpageThemeAction("OpenThemePane");
        }
        addNewComponent(newSection, 0, 0, true);
      }
    }
    // this.setState(prevState => ({ showCoverpage: !prevState.showCoverpage }));
    return null;
  };

  _getDocumentBlocks = userSignUpInfo => {
    const buttons = this._getButtons(this.props.mode, userSignUpInfo);
    return buttons.map(component => {
      let isDisabled = false;
      if (component.id === "add-dealtable") {
        isDisabled =
          this.props.sectionList.findIndex(
            section =>
              section.type === DOCUMENT_COMPONENT_TYPES.DEAL_TABLE ||
              section.type === DOCUMENT_COMPONENT_TYPES.QUOTE_TABLE ||
              section.type === DOCUMENT_COMPONENT_TYPES.QUOTE_TABLE_WRAPPER,
          ) > -1;
      } else if (component.id === "add-quotetable-placeholder") {
        isDisabled =
          this.props.sectionList.findIndex(
            section =>
              section.type === DOCUMENT_COMPONENT_TYPES.QUOTE_TABLE_WRAPPER,
          ) > -1;
      }
      isDisabled = isDisabled || this.props.showDocumentDiffView;
      component.isDisabled = isDisabled;
      return component;
    });
  };

  _getStylingComponents = items => {
    const { showDocumentDiffView, showCoverpage } = this.props;
    const components = items.map(component => (
      <div
        key={component.id}
        className={
          showDocumentDiffView
            ? "add-style-component-item is-disabled"
            : "add-style-component-item"
        }>
        <div
          type="text"
          data-test={`add-style-component-${component.id}`}
          className="add-style-component">
          <div className="add-style-componenttext">
            <i>
              <FontAwesomeIcon icon={component.icon} />
            </i>
            <span id={component.id}>{component.label}</span>
          </div>
          <Tooltip
            placement="top"
            overlay={showCoverpage ? "Hide cover page" : "Show page cover"}
            align={{
              offset: [3, 0],
            }}>
            <div className="add-style-componentarrow">
              <span
                data-test="documentheader-togglediffmode-switch"
                className="documentheader-togglediffmode-switch">
                <Input
                  data-test="stylecomponents-togglediffmode-checkbox"
                  checked={showCoverpage}
                  isSwitch
                  type="checkbox"
                  onChange={event => this._addComponent(event, component.id)}
                />
              </span>
            </div>
          </Tooltip>
        </div>
      </div>
    ));
    return components;
  };

  // drag start
  // it could be a new component
  // or just re-ordering the existing section
  _onDragStart = (event, id) => {
    const { setDraggedSection } = this.props;
    event.dataTransfer.effectAllowed = "move";
    const section = this._addNewComponent(id);
    const { addComponentType } = this.props;
    let componentType = "new-component";
    if (addComponentType === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER) {
      componentType = "header-footer";
    }
    // This is only for hidding the plus button dropdown
    if (this.blockWrapperRef && this.blockWrapperRef.current) {
      this.blockWrapperRef.current.click();
    }
    setDraggedSection(section, componentType);

    // To get mouse offset for PDF document feature
    const rect = event.target.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    window.documentBlockCursorOffset = { left: x, top: y };
  };

  _onDragEnd = () => {
    const { setDraggedSection } = this.props;
    setDraggedSection(null, null);
  };

  render() {
    const userSignUpInfo = readDataFromLocalStorage("userSignUpInfo");
    const { mode, addComponentType } = this.props;
    const stylingButtons = this._getStylingButtons(mode);
    return (
      <div className="content-scroll">
        {/* All Add Component Buttons Section */}
        <div
          ref={this.blockWrapperRef}
          role="button"
          tabIndex="0"
          className="add-component"
          data-test="addcomponents-add-component">
          <span
            className="addproduct-info add-component-title add-component-desc"
            data-test="addcomponents-add-component-title">
            {ReactHtmlParser(
              this.props.isPDFDocument
                ? "This document is an uploaded PDF. You cannot edit it but can share it with anyone or request eSignatures on it. Drag and drop the Signature block on the document to add signers. <a href='https://youtu.be/XD4RbzxCFas?utm_source=Revv%20app&utm_medium=upload_pdf&utm_campaign=In_app_copy' target='_blank'>Learn more</a>"
                : "To add a block, drag and drop it on the document",
            )}
          </span>
          <AddComponentBlocks
            customClassName="addcomponents-sidebar-blocks"
            blocks={this._getDocumentBlocks(userSignUpInfo)}
            handleDragStart={this._onDragStart}
            handleDragEnd={this._onDragEnd}
            isDraggable
          />
        </div>

        {/* Cover Page Component Button Section */}
        {addComponentType !== DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER && (
          <div className="add-styling-component-wrapper">
            {!this.props.isPDFDocument && (
              <div>
                <span className="add-component-title">Document styling</span>
                <div className="add-style-component-items">
                  {this._getStylingComponents(stylingButtons)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const { mode } = props;
  // let sectionList;
  let documentId = 0;
  let priceListId = 0;
  let currencyName = "USD";
  let isPDFDocument = "";
  const { components } = state.DocumentStore;
  if (mode === "DOCUMENT") {
    const {
      document: { id, pricelist, isPdfDocument } = {},
    } = state.DocumentStore;
    currencyName =
      pricelist && pricelist.currency ? pricelist.currency : currencyName;
    documentId = id;
    isPDFDocument = isPdfDocument;
    priceListId =
      pricelist && pricelist.priceListID ? pricelist.priceListID : priceListId;
  }

  const sectionList = Object.keys(components).map(componentId => ({
    id: componentId,
    type: components[componentId].content.type,
  }));

  // get coverpage section
  const coverpageId = Object.keys(components).find(
    componentId =>
      components[componentId] &&
      components[componentId].content.type ===
        DOCUMENT_COMPONENT_TYPES.COVERPAGE,
  );
  const coverpageData = coverpageId && components[coverpageId];

  let showCoverpage;
  let coverpage;
  if (coverpageData && coverpageData.content) {
    coverpage = coverpageData.content;
    if (
      coverpage.data &&
      coverpage.data.attributes &&
      coverpage.data.attributes.isOpen !== undefined
    )
      showCoverpage = coverpage.data.attributes.isOpen;
    else showCoverpage = true;
  } else {
    showCoverpage = false;
  }

  return {
    currencyName,
    documentId,
    priceListId,
    sectionList,
    coverpage,
    showCoverpage,
    isPDFDocument,
  };
};

export default connect(mapStateToProps)(AddComponents);



// WEBPACK FOOTER //
// ./src/components/DocumentComponents/AddComponents.js